import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import {
  Building2, CalendarCheck, CalendarDays, CreditCard, ExternalLink, LayoutDashboard, LogOut, Mail, Menu,
  Sparkles, SquarePen, Star, Tag, Users, X,
} from "lucide-react";
import Seo from "../components/Seo";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AdminReservations from "../components/admin/AdminReservations";
import AdminCalendar from "../components/admin/AdminCalendar";
import AdminProperty from "../components/admin/AdminProperty";
import AdminPayments from "../components/admin/AdminPayments";
import AdminContent from "../components/admin/AdminContent";
import { AdminAmenities, AdminReviews } from "../components/admin/AdminCatalogue";
import { AdminData } from "./AdminContext";
import Dashboard from "./pages/Dashboard";
import Coupons from "./pages/Coupons";
import Messages from "./pages/Messages";
import GuestsPage from "./pages/Guests";


const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck, badge: "awaiting_payment" },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/property", label: "Property & Rules", icon: Building2 },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/content", label: "Website content", icon: SquarePen },
  { to: "/admin/amenities", label: "Amenities", icon: Sparkles },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/coupons", label: "Coupons", icon: Tag },
  { to: "/admin/guests", label: "Guests & users", icon: Users },
  { to: "/admin/messages", label: "Messages", icon: Mail, badge: "unread_messages" },
];

const TITLES = Object.fromEntries(NAV.map((n) => [n.to, n.label]));

function PageFrame({ title, children, subtitle }) {
  return (
    <div>
      <div className="mb-8">
        <h1 className="font-display text-3xl sm:text-4xl">{title}</h1>
        {subtitle && <p className="text-sm text-white/45 mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Sidebar({ badges, onNavigate }) {
  return (
    <nav aria-label="Admin navigation" className="flex flex-col h-full">
      <Link to="/admin" onClick={onNavigate} className="flex items-center gap-3 px-5 h-16 border-b border-white/[0.06] shrink-0">
        <span className="font-cormorant tracking-[0.25em] text-xl"><span className="text-gold">ROOM</span><span className="text-crimson-bright"> XIII</span></span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-white/30 border border-white/10 px-1.5 py-0.5">Admin</span>
      </Link>
      <ul className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {NAV.map(({ to, label, icon: Icon, end, badge }) => (
          <li key={to}>
            <NavLink to={to} end={end} onClick={onNavigate}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/60 hover:text-white hover:bg-white/[0.04] border-l-2 border-transparent"}`}>
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {badge && badges[badge] > 0 && <span className="text-[10px] bg-crimson-bright text-white px-1.5 py-0.5 rounded-full min-w-[18px] text-center" aria-label={`${badges[badge]} need attention`}>{badges[badge]}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
      <div className="p-4 border-t border-white/[0.06]">
        <Link to="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-white/50 hover:text-gold"><ExternalLink size={13} /> View website</Link>
      </div>
    </nav>
  );
}

export default function AdminApp() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [property, setProperty] = useState(null);
  const [badges, setBadges] = useState({});

  const reloadProperty = useCallback(async () => {
    try { setProperty((await api.get("/admin/properties")).data[0] || null); } catch { /* shown by pages */ }
  }, []);
  const reloadBadges = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/dashboard");
      setBadges({ awaiting_payment: data.totals.awaiting_payment, unread_messages: data.totals.unread_messages });
    } catch { /* badges are optional */ }
  }, []);

  useEffect(() => { reloadProperty(); reloadBadges(); }, [reloadProperty, reloadBadges]);
  useEffect(() => { setOpen(false); reloadBadges(); }, [location.pathname, reloadBadges]);

  const title = TITLES[location.pathname] || "Admin";

  return (
    <AdminData.Provider value={{ property, reloadProperty, badges, reloadBadges }}>
      <Seo title="Admin | ROOM XIII" description="Admin" path="/admin" noindex />
      <div className="min-h-screen grain flex" style={{ background: "var(--bg-base)" }} data-testid="admin-app">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-64 shrink-0 border-r border-white/[0.06] fixed inset-y-0 left-0 z-30" style={{ background: "#090807" }}>
          <Sidebar badges={badges} />
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
            <aside className="absolute inset-y-0 left-0 w-72 border-r border-white/10" style={{ background: "#090807" }}>
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="absolute top-4 right-4 text-white/60"><X size={20} /></button>
              <Sidebar badges={badges} onNavigate={() => setOpen(false)} />
            </aside>
          </div>
        )}

        <div className="flex-1 lg:ml-64 min-w-0">
          <header className="sticky top-0 z-20 h-16 flex items-center justify-between gap-4 px-4 sm:px-8 border-b border-white/[0.06] backdrop-blur" style={{ background: "rgba(9,8,7,0.9)" }}>
            <div className="flex items-center gap-3">
              <button onClick={() => setOpen(true)} aria-label="Open menu" className="lg:hidden text-white/70"><Menu size={22} /></button>
              <span className="text-sm text-white/50 hidden sm:block">{title}</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-white/50 hidden sm:block truncate max-w-[220px]">{user?.email}</span>
              <button onClick={logout} className="flex items-center gap-2 text-white/60 hover:text-gold" data-testid="admin-logout"><LogOut size={15} /> Sign out</button>
            </div>
          </header>

          <main className="p-4 sm:p-8 max-w-[1400px]">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="bookings" element={<PageFrame title="Bookings" subtitle="Search, filter and manage every reservation. Open a booking to confirm payment, check guests in and out, or cancel."><AdminReservations property={property} /></PageFrame>} />
              <Route path="calendar" element={<PageFrame title="Calendar" subtitle="See what is booked, block dates for maintenance or private use."><AdminCalendar property={property} /></PageFrame>} />
              <Route path="property" element={<PageFrame title="Property & Rules" subtitle="Check-in and check-out times, prices, fees, guest limits, cancellation policy and house rules."><AdminProperty property={property} onSaved={reloadProperty} /></PageFrame>} />
              <Route path="payments" element={<PageFrame title="Payments" subtitle="Your payment QR code and how guests pay."><AdminPayments property={property} onSaved={reloadProperty} /></PageFrame>} />
              <Route path="content" element={<PageFrame title="Website content"><AdminContent /></PageFrame>} />
              <Route path="amenities" element={<PageFrame title="Amenities" subtitle="What appears in the amenities list on your site."><AdminAmenities /></PageFrame>} />
              <Route path="reviews" element={<PageFrame title="Reviews" subtitle="Testimonials shown on the home and room pages."><AdminReviews /></PageFrame>} />
              <Route path="coupons" element={<PageFrame title="Coupons"><Coupons /></PageFrame>} />
              <Route path="guests" element={<PageFrame title="Guests & users" subtitle="Everyone with an account. Deactivate an account or give someone admin access."><GuestsPage /></PageFrame>} />
              <Route path="messages" element={<PageFrame title="Messages" subtitle="Enquiries from the contact form."><Messages /></PageFrame>} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </AdminData.Provider>
  );
}
